/**
 * Personal Productivity Chart Component
 *
 * Displays user's productivity timeline
 * Uses centralized dashboard config for titles and labels
 */

import { Loader2 } from 'lucide-react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { Card, CardHeader, CardTitle, CardContent } from '@/presentation/components/ui/Card'
import { useMyProductivityTimeline } from '@/application/dashboard'
import { getDashboardConfig, getTimelineChartTitle } from '@/config/dashboard.config'
import { useAuth } from '@/application/auth'

interface CustomTooltipProps {
  active?: boolean
  payload?: Array<{
    value: number
    payload: { date: string; count: number }
  }>
  tooltipLabel: string
}

function CustomTooltip({ active, payload, tooltipLabel }: CustomTooltipProps) {
  if (active && payload && payload.length) {
    const data = payload[0].payload
    const formattedDate = new Date(data.date).toLocaleDateString('es-CO', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    })
    return (
      <div className="bg-[var(--background)] border border-[var(--border)] rounded-lg p-3 shadow-lg">
        <p className="font-medium text-[var(--foreground)]">{formattedDate}</p>
        <p className="text-sm text-[var(--primary)]">
          {data.count} {tooltipLabel}
        </p>
      </div>
    )
  }
  return null
}

interface PersonalProductivityChartProps {
  days?: number
}

export function PersonalProductivityChart({ days = 30 }: PersonalProductivityChartProps) {
  const { user } = useAuth()
  const { data, isLoading, isError } = useMyProductivityTimeline(days)

  // Get config from centralized dashboard config
  const config = getDashboardConfig(user?.role)
  const title = getTimelineChartTitle(user?.role)
  const tooltipLabel = config.charts.timelineTooltipLabel || 'items'

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[250px]">
            <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
          </div>
        </CardContent>
      </Card>
    )
  }

  if (isError || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[250px] text-[var(--foreground-muted)]">
            Error al cargar datos
          </div>
        </CardContent>
      </Card>
    )
  }

  // Format dates for display
  const chartData = data.timeline.map((item) => ({
    ...item,
    displayDate: new Date(item.date).toLocaleDateString('es-CO', {
      day: '2-digit',
      month: '2-digit',
    }),
  }))

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <div className="flex items-center justify-center h-[250px] text-[var(--foreground-muted)]">
            Sin datos disponibles
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="colorPersonalProductivity" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis
                dataKey="displayDate"
                tick={{ fontSize: 11, fill: 'var(--foreground-muted)' }}
                tickLine={false}
                axisLine={{ stroke: 'var(--border)' }}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 11, fill: 'var(--foreground-muted)' }}
                tickLine={false}
                axisLine={{ stroke: 'var(--border)' }}
                allowDecimals={false}
                label={
                  config.charts.timelineYAxisLabel
                    ? {
                        value: config.charts.timelineYAxisLabel,
                        angle: -90,
                        position: 'insideLeft',
                        style: { fontSize: 11, fill: 'var(--foreground-muted)' },
                      }
                    : undefined
                }
              />
              <Tooltip content={<CustomTooltip tooltipLabel={tooltipLabel} />} />
              <Area
                type="monotone"
                dataKey="count"
                stroke="#22c55e"
                strokeWidth={2}
                fill="url(#colorPersonalProductivity)"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}
